/**
 * The Workbench's case list (vNext-api `docs/workbench.md`, "The case
 * list"): every live case and the ones lately released, completed or handed
 * back — searched, filtered, ordered and paged by the API — polled on its
 * version: a 304 while nothing on any case moved.
 */

import { useRef } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type { WorkbenchCaseList, WorkbenchCaseQuery } from '@/types/workbench';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

/** The list moves with any case's event, run, reply or ask. */
export const WORKBENCH_CASES_POLL_MS = 30_000;

/**
 * The query is a `refetchDeps` entry, so a filter, sort or page change drops
 * the stored ETag: a different query is a different token space.
 */
export function useWorkbenchCases(query: WorkbenchCaseQuery = {}) {
  const key = JSON.stringify(query);
  const queryRef = useRef(query);
  queryRef.current = query;

  const poll = usePoll<WorkbenchCaseList>({
    interval: WORKBENCH_CASES_POLL_MS,
    refetchDeps: [key],
    queryFn: async ({ signal, etag, manual }) => {
      const result = await vnextApiClient.listWorkbenchCases(queryRef.current, {
        signal,
        etag: manual ? null : etag,
      });
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });
  return {
    list: poll.data ?? null,
    error: poll.error === undefined ? null : toError(poll.error),
    isLoading: poll.isLoading,
    refetch: poll.refetch,
  };
}
